/* eslint-disable */

import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  defaultDataIdFromObject,
  StoreObject,
} from '@apollo/client';
import { onError } from '@apollo/link-error';
import { createUploadLink } from 'apollo-upload-client';
import { captureException } from '../Sentry';
import { relayStylePagination } from '@apollo/client/utilities';

export const createApolloClient = (apiHost: string) =>
  new ApolloClient({
    cache: createCache(),
    link: ApolloLink.from([
      createErrorLink(),
      createUploadLink({
        uri: `${apiHost}/graphql`,
        credentials: 'include',
      }),
    ]),
  });

export function dataIdFromObject(obj: Readonly<StoreObject>) {
  return defaultDataIdFromObject(obj);
}

export function createCache() {
  return new InMemoryCache({
    dataIdFromObject,
    typePolicies: {
      // A contract item's choices, options and addons are copies of the menu
      // item's, stored as JSON on the contract item and keeping the menu item's
      // ids. Two contract items built from the same menu item (or one contract
      // item and the menu item itself) therefore carry MenuItemChoice objects
      // with the same id but different names, prices and option lists. The
      // default keyFields would merge them all into one MenuItemChoice:<id>
      // entity, so every fetch of one overwrote the others in the cache: a
      // customized Day 2 item would show the menu defaults or Day 3's edits,
      // and saving the drawer then persisted them (PV-8709). keyFields: false
      // stores them embedded in the contract item or menu item they belong to.
      MenuItemChoice: { keyFields: false },
      MenuItemOption: { keyFields: false },
      MenuItemAddon: { keyFields: false },
      // Same reasoning for the guest proposal copies of those objects.
      GuestProposalMenuChoice: { keyFields: false },
      GuestProposalMenuOption: { keyFields: false },
      GuestProposalMenuAddon: { keyFields: false },
      Query: {
        fields: {
          activeEvents: relayStylePagination(),
          // Key by filter args so New vs Archived (and other filters) keep
          // separate cache entries — otherwise tab switches flash the wrong list.
          inboxEvents: relayStylePagination([
            'venueIds',
            'ownerIds',
            'unread',
            'archived',
          ]),
          // Keyed the same way inboxEvents is: the venue picker and the
          // read filter each need their own cache entry.
          teamMessageMentions: relayStylePagination(['venueIds', 'unread']),
          eventDiscussionComments: relayStylePagination(),
          pendingExpressBookRequests: {
            merge(existing = {}, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
          venueTasks: relayStylePagination(),
          eventTasks: relayStylePagination(),
          marketplaceSpaces: relayStylePagination(),
          venueCollections: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          messages: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          message: {
            merge(existing = {}, incoming) {
              return { ...incoming };
            },
          },
        },
      },
      Contract: {
        fields: {
          contractItems: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
        },
      },

      VenueCollection: {
        fields: {
          venues: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
        },
      },
      Venue: {
        fields: {
          emailTemplates: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          documents: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
        },
      },
      Organization: {
        fields: {
          emailTemplates: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          documents: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          venueCollections: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          paymentMethods: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
        },
      },
      Message: {
        fields: {
          documents: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          ccEmailAddresses: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          toEmailAddresses: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
        },
      },
      Event: {
        fields: {
          documents: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          offlineCommunications: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          messages: {
            merge(existing = [], incoming: any[]) {
              if (incoming.length === 0) {
                return existing;
              }
              return [...incoming];
            },
          },
          additionalContacts: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          spaces: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          sessions: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
          contracts: {
            merge(existing = [], incoming: any[]) {
              return [...incoming];
            },
          },
        },
      },
      MultisearchResult: {
        fields: {
          events: {
            merge(existing = [], incoming: any[]) {
              if (incoming.length === 0) {
                return existing;
              }
              return [...incoming];
            },
          },
        },
      },
      Address: {
        merge(existing = {}, incoming) {
          return { ...incoming };
        },
      },
    },
  });
}

function createErrorLink() {
  return onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path, ...rest }) => {
        const error = `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations
        )}, Path: ${path}, Rest: ${JSON.stringify(rest)}`;
        console.log(error);
        captureException(new Error(error));
      });
    }
    if (networkError) {
      const error = `[Network error]: ${networkError}`;
      console.log(error);
      captureException(new Error(error));
    }
  });
}
